import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectClient,
  selectPolicyApplications,
  selectPolicyApplicationsStatus,
  selectSessionAdvisor,
} from '@client';
import { deriveApplicationStatus } from '../pages/PolicyApplicationDashboard';
import {
  fundingPolicyTypeDefs,
  getFormIdForPolicyTypeAndState,
  policyTypeUnderwritingFormId,
  UnitedStatesState,
} from '@shared';
import { useFormIdFromApi } from './useFormIdFromApi';

export function useClientPolicyApplication() {
  const { clientId, clientPolicyApplicationId } = useParams<{
    clientId?: string;
    clientPolicyApplicationId?: string;
  }>();

  const client = useSelector(selectClient);
  const policyApplications = useSelector(selectPolicyApplications);
  const policyApplicationsStatus = useSelector(selectPolicyApplicationsStatus);
  const currentAdvisor = useSelector(selectSessionAdvisor);

  const policyApplication = useMemo(
    () =>
      policyApplications.find(app => app.clientPolicyApplicationId === clientPolicyApplicationId),
    [policyApplications, clientPolicyApplicationId],
  );

  const fundingSourceId = policyApplication?.fundingSourceId;
  const policyFundingSource = useMemo(() => {
    if (!fundingSourceId || !client?.multipleFundingSources?.ltcPolicy) {
      return undefined;
    }
    return client.multipleFundingSources.ltcPolicy[fundingSourceId];
  }, [fundingSourceId, client]);
  const policyType = policyApplication?.fundingPolicyType;
  const fundingSourceDef = policyType ? fundingPolicyTypeDefs[policyType] : null;
  const steps = policyApplication?.steps;
  const applicationStatus = policyApplication
    ? deriveApplicationStatus(policyApplication).status
    : null;

  const underwritingFormId = policyType ? policyTypeUnderwritingFormId[policyType] : undefined;
  
  // Fetch application form ID from API
  const { formId: apiApplicationFormId, isLoading: isLoadingFormId } = useFormIdFromApi({
    stateCode: policyApplication?.stateOfResidenceAbbreviation || 'default',
    formType: 'application-form',
    fundingPolicyTypeId: policyType,
    enabled: !!policyApplication?.stateOfResidenceAbbreviation && !!policyType,
  });

  // Fallback to static form ID if API fails or is loading
  const staticApplicationFormId =
    policyApplication?.stateOfResidenceAbbreviation && policyType
      ? getFormIdForPolicyTypeAndState(
          policyType,
          policyApplication.stateOfResidenceAbbreviation as keyof typeof UnitedStatesState,
          'application-form',
        )
      : undefined;

  // Use API form ID if available, otherwise use static fallback
  const applicationFormId = apiApplicationFormId || staticApplicationFormId;

  const currentStep = useMemo(() => {
    if (!steps) return null;

    const inProgressStep = steps.find(step => step.status === 'in_progress');
    if (inProgressStep) return inProgressStep;

    const notStartedStep = steps.find(step => step.status === 'not_started');
    if (notStartedStep) return notStartedStep;

    const blockedStep = steps.find(step => step.status === 'blocked');
    if (blockedStep) return blockedStep;

    return steps[0];
  }, [steps]);

  const allStepsCompleted =
    steps?.every(step => step.status === 'completed' || step.status === 'skipped') || false;

  const isLoading =
    policyApplicationsStatus === 'fetching' ||
    policyApplicationsStatus === 'creatingPolicyApplication' ||
    (!policyApplication && policyApplicationsStatus !== 'error');

  const clientInfo = useMemo(
    () => ({
      primaryName: `${client.clientFirstName} ${client.clientLastName}`,
      primaryEmail: client.clientEmail,
      primaryUserId: client.clientId,
      secondaryName: client.mutableClientPartner
        ? `${client.mutableClientPartner.clientFirstName} ${client.mutableClientPartner.clientLastName}`
        : undefined,
      secondaryEmail: client.mutableClientPartner?.clientEmail,
      secondaryUserId: client.mutableClientPartner?.clientId,
      advisorName: currentAdvisor
        ? `${currentAdvisor.advisorFirstName} ${currentAdvisor.advisorLastName}`
        : undefined,
      advisorEmail: currentAdvisor?.advisorEmail,
      advisorUserId: currentAdvisor?.advisorId,
    }),
    [client, currentAdvisor],
  );

  return {
    // Route params
    clientId,
    clientPolicyApplicationId,

    // Data
    client,
    policyApplication,
    policyType,
    fundingSourceDef,
    steps,
    currentStep,
    applicationStatus,
    currentAdvisor,
    clientInfo,
    policyFundingSource,

    // Form IDs
    underwritingFormId,
    applicationFormId,

    // State flags
    isLoading,
    allStepsCompleted,

    // Status
    policyApplicationsStatus,
  };
}
